// Phase 6: Silent-e (Magic-e / CVCe pattern)
// The silent e at the end makes the vowel "say its name"

import type { Phase } from "../../../../types/literacy"

export const phase6: Phase = {
  id: "phase-6",
  number: 6,
  title: "Silent-e Words",
  description: "Learn how silent e makes the vowel say its name (CVCe pattern)",
  prerequisites: ["phase-5"],
  lessons: [
    {
      id: "lesson-6-1",
      phaseId: "phase-6",
      title: "Silent-e with A (a_e)",
      description: "Learn words where silent-e makes 'a' say /ā/",
      drills: [
        {
          type: "visual",
          items: [
            { id: "ae-rule", type: "word", content: "a_e", audioHint: "When we see a_e, the a says its name: /ā/" },
          ],
          masteryThreshold: 90,
          consecutiveCorrect: 3,
        },
        {
          type: "auditory",
          items: [
            { id: "ae-1", type: "word", content: "cake", audioHint: "/k/ /ā/ /k/ - cake" },
            { id: "ae-2", type: "word", content: "make", audioHint: "/m/ /ā/ /k/ - make" },
            { id: "ae-3", type: "word", content: "take", audioHint: "/t/ /ā/ /k/ - take" },
            { id: "ae-4", type: "word", content: "lake", audioHint: "/l/ /ā/ /k/ - lake" },
            { id: "ae-5", type: "word", content: "bake", audioHint: "/b/ /ā/ /k/ - bake" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
        {
          type: "blending",
          items: [],
          blendingWords: [
            // Real words
            { word: "cake", type: "real", pattern: "CVCe" },
            { word: "make", type: "real", pattern: "CVCe" },
            { word: "take", type: "real", pattern: "CVCe" },
            { word: "lake", type: "real", pattern: "CVCe" },
            { word: "bake", type: "real", pattern: "CVCe" },
            { word: "wake", type: "real", pattern: "CVCe" },
            { word: "sake", type: "real", pattern: "CVCe" },
            { word: "fake", type: "real", pattern: "CVCe" },
            { word: "name", type: "real", pattern: "CVCe" },
            { word: "same", type: "real", pattern: "CVCe" },
            { word: "game", type: "real", pattern: "CVCe" },
            { word: "fame", type: "real", pattern: "CVCe" },
            { word: "came", type: "real", pattern: "CVCe" },
            { word: "tame", type: "real", pattern: "CVCe" },
            { word: "late", type: "real", pattern: "CVCe" },
            { word: "gate", type: "real", pattern: "CVCe" },
            { word: "date", type: "real", pattern: "CVCe" },
            { word: "rate", type: "real", pattern: "CVCe" },
            { word: "made", type: "real", pattern: "CVCe" },
            { word: "fade", type: "real", pattern: "CVCe" },
            { word: "wade", type: "real", pattern: "CVCe" },
            { word: "save", type: "real", pattern: "CVCe" },
            { word: "wave", type: "real", pattern: "CVCe" },
            { word: "cave", type: "real", pattern: "CVCe" },
            // Nonsense words (40%)
            { word: "dake", type: "nonsense", pattern: "CVCe" },
            { word: "vake", type: "nonsense", pattern: "CVCe" },
            { word: "zake", type: "nonsense", pattern: "CVCe" },
            { word: "rame", type: "nonsense", pattern: "CVCe" },
            { word: "vame", type: "nonsense", pattern: "CVCe" },
            { word: "zame", type: "nonsense", pattern: "CVCe" },
            { word: "tade", type: "nonsense", pattern: "CVCe" },
            { word: "gade", type: "nonsense", pattern: "CVCe" },
            { word: "pate", type: "nonsense", pattern: "CVCe" },
            { word: "vate", type: "nonsense", pattern: "CVCe" },
            { word: "zave", type: "nonsense", pattern: "CVCe" },
            { word: "lave", type: "nonsense", pattern: "CVCe" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
    },
    {
      id: "lesson-6-2",
      phaseId: "phase-6",
      title: "Silent-e with I (i_e)",
      description: "Learn words where silent-e makes 'i' say /ī/",
      prerequisites: ["lesson-6-1"],
      drills: [
        {
          type: "visual",
          items: [
            { id: "ie-rule", type: "word", content: "i_e", audioHint: "When we see i_e, the i says its name: /ī/" },
          ],
          masteryThreshold: 90,
          consecutiveCorrect: 3,
        },
        {
          type: "auditory",
          items: [
            { id: "ie-1", type: "word", content: "bike", audioHint: "/b/ /ī/ /k/ - bike" },
            { id: "ie-2", type: "word", content: "like", audioHint: "/l/ /ī/ /k/ - like" },
            { id: "ie-3", type: "word", content: "hike", audioHint: "/h/ /ī/ /k/ - hike" },
            { id: "ie-4", type: "word", content: "time", audioHint: "/t/ /ī/ /m/ - time" },
            { id: "ie-5", type: "word", content: "dime", audioHint: "/d/ /ī/ /m/ - dime" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
        {
          type: "blending",
          items: [],
          blendingWords: [
            // Real words
            { word: "bike", type: "real", pattern: "CVCe" },
            { word: "like", type: "real", pattern: "CVCe" },
            { word: "hike", type: "real", pattern: "CVCe" },
            { word: "mike", type: "real", pattern: "CVCe" },
            { word: "time", type: "real", pattern: "CVCe" },
            { word: "dime", type: "real", pattern: "CVCe" },
            { word: "lime", type: "real", pattern: "CVCe" },
            { word: "mine", type: "real", pattern: "CVCe" },
            { word: "nine", type: "real", pattern: "CVCe" },
            { word: "fine", type: "real", pattern: "CVCe" },
            { word: "line", type: "real", pattern: "CVCe" },
            { word: "pine", type: "real", pattern: "CVCe" },
            { word: "wine", type: "real", pattern: "CVCe" },
            { word: "dine", type: "real", pattern: "CVCe" },
            { word: "vine", type: "real", pattern: "CVCe" },
            { word: "side", type: "real", pattern: "CVCe" },
            { word: "hide", type: "real", pattern: "CVCe" },
            { word: "ride", type: "real", pattern: "CVCe" },
            { word: "wide", type: "real", pattern: "CVCe" },
            { word: "five", type: "real", pattern: "CVCe" },
            { word: "hive", type: "real", pattern: "CVCe" },
            { word: "dive", type: "real", pattern: "CVCe" },
            { word: "life", type: "real", pattern: "CVCe" },
            { word: "wife", type: "real", pattern: "CVCe" },
            // Nonsense words
            { word: "tike", type: "nonsense", pattern: "CVCe" },
            { word: "zike", type: "nonsense", pattern: "CVCe" },
            { word: "vime", type: "nonsense", pattern: "CVCe" },
            { word: "zime", type: "nonsense", pattern: "CVCe" },
            { word: "tine", type: "nonsense", pattern: "CVCe" },
            { word: "zine", type: "nonsense", pattern: "CVCe" },
            { word: "bide", type: "nonsense", pattern: "CVCe" },
            { word: "gide", type: "nonsense", pattern: "CVCe" },
            { word: "vide", type: "nonsense", pattern: "CVCe" },
            { word: "zive", type: "nonsense", pattern: "CVCe" },
            { word: "bife", type: "nonsense", pattern: "CVCe" },
            { word: "dife", type: "nonsense", pattern: "CVCe" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
    },
    {
      id: "lesson-6-3",
      phaseId: "phase-6",
      title: "Silent-e with O (o_e)",
      description: "Learn words where silent-e makes 'o' say /ō/",
      prerequisites: ["lesson-6-2"],
      drills: [
        {
          type: "visual",
          items: [
            { id: "oe-rule", type: "word", content: "o_e", audioHint: "When we see o_e, the o says its name: /ō/" },
          ],
          masteryThreshold: 90,
          consecutiveCorrect: 3,
        },
        {
          type: "auditory",
          items: [
            { id: "oe-1", type: "word", content: "home", audioHint: "/h/ /ō/ /m/ - home" },
            { id: "oe-2", type: "word", content: "bone", audioHint: "/b/ /ō/ /n/ - bone" },
            { id: "oe-3", type: "word", content: "cone", audioHint: "/k/ /ō/ /n/ - cone" },
            { id: "oe-4", type: "word", content: "hope", audioHint: "/h/ /ō/ /p/ - hope" },
            { id: "oe-5", type: "word", content: "rope", audioHint: "/r/ /ō/ /p/ - rope" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
        {
          type: "blending",
          items: [],
          blendingWords: [
            // Real words
            { word: "home", type: "real", pattern: "CVCe" },
            { word: "dome", type: "real", pattern: "CVCe" },
            { word: "bone", type: "real", pattern: "CVCe" },
            { word: "cone", type: "real", pattern: "CVCe" },
            { word: "tone", type: "real", pattern: "CVCe" },
            { word: "zone", type: "real", pattern: "CVCe" },
            { word: "lone", type: "real", pattern: "CVCe" },
            { word: "hope", type: "real", pattern: "CVCe" },
            { word: "rope", type: "real", pattern: "CVCe" },
            { word: "cope", type: "real", pattern: "CVCe" },
            { word: "dope", type: "real", pattern: "CVCe" },
            { word: "mope", type: "real", pattern: "CVCe" },
            { word: "nose", type: "real", pattern: "CVCe" },
            { word: "rose", type: "real", pattern: "CVCe" },
            { word: "hose", type: "real", pattern: "CVCe" },
            { word: "dose", type: "real", pattern: "CVCe" },
            { word: "pose", type: "real", pattern: "CVCe" },
            { word: "note", type: "real", pattern: "CVCe" },
            { word: "vote", type: "real", pattern: "CVCe" },
            { word: "poke", type: "real", pattern: "CVCe" },
            { word: "joke", type: "real", pattern: "CVCe" },
            { word: "woke", type: "real", pattern: "CVCe" },
            { word: "hole", type: "real", pattern: "CVCe" },
            { word: "pole", type: "real", pattern: "CVCe" },
            { word: "role", type: "real", pattern: "CVCe" },
            { word: "sole", type: "real", pattern: "CVCe" },
            // Nonsense words
            { word: "bome", type: "nonsense", pattern: "CVCe" },
            { word: "vome", type: "nonsense", pattern: "CVCe" },
            { word: "fone", type: "nonsense", pattern: "CVCe" },
            { word: "pone", type: "nonsense", pattern: "CVCe" },
            { word: "vone", type: "nonsense", pattern: "CVCe" },
            { word: "bope", type: "nonsense", pattern: "CVCe" },
            { word: "tope", type: "nonsense", pattern: "CVCe" },
            { word: "vope", type: "nonsense", pattern: "CVCe" },
            { word: "bose", type: "nonsense", pattern: "CVCe" },
            { word: "fose", type: "nonsense", pattern: "CVCe" },
            { word: "vose", type: "nonsense", pattern: "CVCe" },
            { word: "zote", type: "nonsense", pattern: "CVCe" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
    },
    {
      id: "lesson-6-4",
      phaseId: "phase-6",
      title: "Silent-e with U (u_e)",
      description: "Learn words where silent-e makes 'u' say /ū/ or /yoo/",
      prerequisites: ["lesson-6-3"],
      drills: [
        {
          type: "visual",
          items: [
            { id: "ue-rule", type: "word", content: "u_e", audioHint: "When we see u_e, the u says its name: /ū/ or /yoo/" },
          ],
          masteryThreshold: 90,
          consecutiveCorrect: 3,
        },
        {
          type: "auditory",
          items: [
            { id: "ue-1", type: "word", content: "cute", audioHint: "/k/ /yoo/ /t/ - cute" },
            { id: "ue-2", type: "word", content: "mute", audioHint: "/m/ /yoo/ /t/ - mute" },
            { id: "ue-3", type: "word", content: "tube", audioHint: "/t/ /oo/ /b/ - tube" },
            { id: "ue-4", type: "word", content: "cube", audioHint: "/k/ /yoo/ /b/ - cube" },
            { id: "ue-5", type: "word", content: "huge", audioHint: "/h/ /yoo/ /j/ - huge" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
        {
          type: "blending",
          items: [],
          blendingWords: [
            // Real words
            { word: "cute", type: "real", pattern: "CVCe" },
            { word: "mute", type: "real", pattern: "CVCe" },
            { word: "tube", type: "real", pattern: "CVCe" },
            { word: "cube", type: "real", pattern: "CVCe" },
            { word: "huge", type: "real", pattern: "CVCe" },
            { word: "fuse", type: "real", pattern: "CVCe" },
            { word: "use", type: "real", pattern: "VCe" },
            { word: "mule", type: "real", pattern: "CVCe" },
            { word: "rule", type: "real", pattern: "CVCe" },
            { word: "rude", type: "real", pattern: "CVCe" },
            { word: "dude", type: "real", pattern: "CVCe" },
            { word: "dune", type: "real", pattern: "CVCe" },
            { word: "tune", type: "real", pattern: "CVCe" },
            { word: "June", type: "real", pattern: "CVCe" },
            // Nonsense words
            { word: "bute", type: "nonsense", pattern: "CVCe" },
            { word: "vute", type: "nonsense", pattern: "CVCe" },
            { word: "zute", type: "nonsense", pattern: "CVCe" },
            { word: "bube", type: "nonsense", pattern: "CVCe" },
            { word: "vube", type: "nonsense", pattern: "CVCe" },
            { word: "zuge", type: "nonsense", pattern: "CVCe" },
            { word: "buse", type: "nonsense", pattern: "CVCe" },
            { word: "guse", type: "nonsense", pattern: "CVCe" },
            { word: "bune", type: "nonsense", pattern: "CVCe" },
            { word: "fune", type: "nonsense", pattern: "CVCe" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
    },
    {
      id: "lesson-6-5",
      phaseId: "phase-6",
      title: "Silent-e Review",
      description: "Practice all silent-e patterns mixed together",
      prerequisites: ["lesson-6-4"],
      drills: [
        {
          type: "blending",
          items: [],
          blendingWords: [
            // Mixed real words from all patterns
            { word: "cake", type: "real", pattern: "CVCe" },
            { word: "bike", type: "real", pattern: "CVCe" },
            { word: "home", type: "real", pattern: "CVCe" },
            { word: "cute", type: "real", pattern: "CVCe" },
            { word: "name", type: "real", pattern: "CVCe" },
            { word: "time", type: "real", pattern: "CVCe" },
            { word: "bone", type: "real", pattern: "CVCe" },
            { word: "tube", type: "real", pattern: "CVCe" },
            { word: "lake", type: "real", pattern: "CVCe" },
            { word: "nine", type: "real", pattern: "CVCe" },
            { word: "hope", type: "real", pattern: "CVCe" },
            { word: "huge", type: "real", pattern: "CVCe" },
            { word: "made", type: "real", pattern: "CVCe" },
            { word: "hide", type: "real", pattern: "CVCe" },
            { word: "nose", type: "real", pattern: "CVCe" },
            { word: "rule", type: "real", pattern: "CVCe" },
            // Mixed nonsense words
            { word: "dake", type: "nonsense", pattern: "CVCe" },
            { word: "zike", type: "nonsense", pattern: "CVCe" },
            { word: "vome", type: "nonsense", pattern: "CVCe" },
            { word: "bute", type: "nonsense", pattern: "CVCe" },
            { word: "rame", type: "nonsense", pattern: "CVCe" },
            { word: "tine", type: "nonsense", pattern: "CVCe" },
            { word: "fone", type: "nonsense", pattern: "CVCe" },
            { word: "zuge", type: "nonsense", pattern: "CVCe" },
          ],
          masteryThreshold: 85,
          consecutiveCorrect: 3,
        },
      ],
    },
  ],
}
